import React from "react";
import { Box, Divider, Typography } from "@mui/material";
import { PaymentStatusBadge } from "./PaymentStatusBadge";
import type { Booking } from "../../api/bookings";

function formatMinorUnits(amount: number | null, currency: string | null): string {
  if (amount === null) return "—";
  const divisor = 100;
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: currency ?? "AUD",
  }).format(amount / divisor);
}

interface PaymentDetailSectionProps {
  booking: Booking;
}

export const PaymentDetailSection: React.FC<PaymentDetailSectionProps> = ({
  booking,
}) => {
  if (!booking.payment_required) return null;

  const purposeLabel =
    booking.payment_purpose === "deposit"
      ? "Deposit"
      : booking.payment_purpose === "full_payment"
      ? "Full payment"
      : null;

  return (
    <Box aria-label="Payment details">
      <Divider sx={{ my: 2 }} />
      <Typography
        variant="subtitle2"
        sx={{ mb: 1.5, fontWeight: 600 }}
      >
        Payments
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="body2" color="text.secondary">
            Status
          </Typography>
          <PaymentStatusBadge status={booking.payment_status} />
        </Box>

        {purposeLabel && (
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2" color="text.secondary">
              Type
            </Typography>
            <Typography variant="body2">{purposeLabel}</Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="body2" color="text.secondary">
            Amount charged
          </Typography>
          <Typography variant="body2">
            {formatMinorUnits(booking.amount_due_minor_units, booking.currency)}
          </Typography>
        </Box>

        {booking.balance_due_minor_units !== null &&
          booking.balance_due_minor_units > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">
                Balance due
              </Typography>
              <Typography variant="body2" color="warning.main">
                {formatMinorUnits(
                  booking.balance_due_minor_units,
                  booking.currency
                )}
              </Typography>
            </Box>
          )}

        {booking.refund_amount_minor_units !== null && (
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2" color="text.secondary">
              Refunded
            </Typography>
            <Typography variant="body2">
              {formatMinorUnits(
                booking.refund_amount_minor_units,
                booking.currency
              )}
            </Typography>
          </Box>
        )}

        {booking.paid_at && (
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2" color="text.secondary">
              Paid at
            </Typography>
            <Typography variant="body2">
              {new Intl.DateTimeFormat("en-AU", {
                dateStyle: "medium",
                timeStyle: "short",
              }).format(new Date(booking.paid_at))}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
